import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Download, FileText, Loader, RefreshCw, Upload, X } from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';
import { buildDisplayedSectionChangeSummary } from '../../utils/brdChangeSummary';
import { JobProgressCard } from '../jobs/JobProgressCard';
import { useJobs } from '../jobs/JobProvider';

const BRD_DOCUMENT_ACCEPT = [
  '.pdf',
  '.doc',
  '.docx',
  'application/pdf',
  'application/msword',
  'application/vnd.ms-word',
  'application/x-msword',
  'application/word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
].join(',');

export const SectionExtractionStep = ({
  status,
  bulkJob = null,
  bulkItems = [],
  dgsConfig,
  reusableSections = null,
  brdChangeAnalysis = null,
  brdUpdatePending = false,
  canUploadUpdatedBrd = false,
  brdUpdateBlockedReason = '',
  baseBrd = null,
  documentName = '',
  pdfId = '',
  extractionMode = 'auto',
  onUploadUpdatedBrd,
  onUploadDgs,
  onDownloadDgsTemplate,
  onClearDgs,
  onExtractionModeChange,
  onExtract,
  onUseReusableSections,
  onBrdChangeAnalysisUpdate,
  onTocStructureApproved,
}) => {
  const { jobs, itemsById, cancel, retry } = useJobs();
  const extractionJob = jobs.find((job) => (
    job.type === 'document_extraction' && String(job.source_id || '') === String(pdfId || '')
  ));
  const isExtracting = status === 'extracting';
  const [isUploadingDgs, setIsUploadingDgs] = useState(false);
  const [dgsUploadError, setDgsUploadError] = useState(null);
  const [dgsTemplateError, setDgsTemplateError] = useState(null);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updatedBrdFile, setUpdatedBrdFile] = useState(null);
  const [updatedBrdError, setUpdatedBrdError] = useState('');
  const [tocStructureContext, setTocStructureContext] = useState(null);
  const [tocStructureModalOpen, setTocStructureModalOpen] = useState(false);
  const [tocStructureApproved, setTocStructureApproved] = useState(false);
  const [tocStructureLoading, setTocStructureLoading] = useState(false);
  const [tocStructureApplying, setTocStructureApplying] = useState(false);
  const [tocStructureError, setTocStructureError] = useState('');
  const [tocStructureAutoOpenKey, setTocStructureAutoOpenKey] = useState('');
  const dgsInputRef = useRef(null);

  const isUploadingUpdatedBrd = status === 'uploading';
  const isBusy = isExtracting || isUploadingDgs || isUploadingUpdatedBrd;
  const hasDgsConfig = Boolean(dgsConfig?.filename);
  const reusableSectionsAvailable = Boolean(reusableSections?.available);
  const reusableSectionsStale = Boolean(reusableSections?.stale);
  const {
    comparison,
    summary: rawChangeSummary,
    displaySummary: changeSummary,
    usesGeneratedSectionSummary,
  } = buildDisplayedSectionChangeSummary(brdChangeAnalysis);
  const comparisonStatus = String(comparison?.status || '').trim().toLowerCase();
  const structureHealth = comparison?.structure_health || {};
  const structureStatus = String(structureHealth?.status || changeSummary?.structure_status || '').trim().toLowerCase();
  const isStructureBlocked = comparisonStatus === 'structure_changed' || structureStatus === 'blocked';
  const isStructureWarning = !isStructureBlocked && structureStatus === 'warning';
  const structureMetrics = structureHealth?.metrics || {};
  const structureMessage = structureHealth?.message || comparison?.message || '';
  const oldBrdVersion = Number(changeSummary?.old_version || 0);
  const newBrdVersion = Number(changeSummary?.new_version || 0);
  const hasVersionDelta = Number.isFinite(oldBrdVersion) && Number.isFinite(newBrdVersion)
    && oldBrdVersion > 0
    && newBrdVersion > oldBrdVersion;
  const isPendingBrdVersionExtraction = Boolean(
    hasVersionDelta
    && (brdUpdatePending || changeSummary?.status === 'pending_extraction')
  );
  const hasChangeSummary = Boolean(
    hasVersionDelta
    && changeSummary
    && Number(changeSummary.total_changes || 0) >= 0
    && !isPendingBrdVersionExtraction
  );
  const changeSummaryPanelClass = isStructureBlocked || isStructureWarning
    ? 'border-t border-amber-200 bg-amber-50/90 px-5 py-4 md:px-6'
    : 'border-t border-blue-200 bg-blue-50/80 px-5 py-4 md:px-6';
  const changeSummaryIconClass = isStructureBlocked || isStructureWarning
    ? 'bg-amber-100 text-amber-700'
    : 'bg-blue-100 text-blue-700';
  const changeSummaryBodyClass = isStructureBlocked || isStructureWarning
    ? 'text-amber-900'
    : 'text-blue-800';
  const blockedUpdateReason = brdUpdateBlockedReason || baseBrd?.brd_update_blocked_reason || '';
  const reusableItemLabel = reusableSections?.itemType === 'features' ? 'features' : 'sections';
  const reusableCount = Number(reusableSections?.count || 0);
  const extractionModeLabel = extractionMode === 'features'
    ? 'Find Features'
    : 'Document Outline';
  const baseBrdLabel = baseBrd?.pdf_file || baseBrd?.document_name || documentName || baseBrd?.pdf_id || pdfId || 'Selected BRD';
  const baseBrdVersion = baseBrd?.brd_version || baseBrd?.version || 1;
  const numericBaseBrdVersion = Number(baseBrdVersion);
  const nextBrdVersion = Number.isFinite(numericBaseBrdVersion) ? numericBaseBrdVersion + 1 : '-';
  const hasApprovedTocStructure = Boolean(
    brdChangeAnalysis?.toc_structure_review?.approved
    || brdChangeAnalysis?.review_decisions?.toc_structure_review?.approved
    || tocStructureContext?.structure_review?.approved
  );
  const needsTocStructureApproval = Boolean(
    hasChangeSummary
    && isStructureBlocked
    && !hasApprovedTocStructure
  );
  const tocStructureReviewKey = `${String(pdfId || '').trim()}:${newBrdVersion || ''}`;

  const readApiError = useCallback(async (response, fallback) => {
    try {
      const payload = await response.json();
      return payload?.detail || payload?.message || fallback;
    } catch (_error) {
      return fallback;
    }
  }, []);

  const loadTocStructureContext = useCallback(async () => {
    const normalizedPdfId = String(pdfId || '').trim();
    if (!normalizedPdfId || !Number.isFinite(newBrdVersion) || newBrdVersion <= 1) {
      setTocStructureError('BRD version context is unavailable for TOC review.');
      return;
    }

    setTocStructureLoading(true);
    setTocStructureError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.BRD_VERSION_TOC_STRUCTURE(normalizedPdfId, newBrdVersion)}`);
      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to load TOC structure review.'));
      }
      const data = await response.json();
      setTocStructureContext(data);
      setTocStructureApproved(Boolean(data?.structure_review?.approved));
      setTocStructureModalOpen(true);
    } catch (error) {
      setTocStructureError(error?.message || 'Failed to load TOC structure review.');
    } finally {
      setTocStructureLoading(false);
    }
  }, [newBrdVersion, pdfId, readApiError]);

  const openTocStructureReview = useCallback(() => {
    if (tocStructureContext) {
      setTocStructureError('');
      setTocStructureApproved(Boolean(tocStructureContext?.structure_review?.approved));
      setTocStructureModalOpen(true);
      return;
    }
    loadTocStructureContext();
  }, [loadTocStructureContext, tocStructureContext]);

  const approveTocStructure = async () => {
    const normalizedPdfId = String(pdfId || '').trim();
    if (!tocStructureApproved) {
      setTocStructureError('Confirm that you reviewed both TOCs before continuing.');
      return;
    }
    if (!normalizedPdfId || !Number.isFinite(newBrdVersion) || newBrdVersion <= 1) {
      setTocStructureError('BRD version context is unavailable for TOC approval.');
      return;
    }

    setTocStructureApplying(true);
    setTocStructureError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.BRD_VERSION_TOC_STRUCTURE_APPROVE(normalizedPdfId, newBrdVersion)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved: true }),
      });
      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to approve TOC structure.'));
      }
      const data = await response.json();
      setTocStructureApproved(true);
      setTocStructureContext((current) => ({
        ...(current || {}),
        structure_review: data?.structure_review || {},
      }));
      if (data?.change_analysis) {
        onBrdChangeAnalysisUpdate?.(data.change_analysis);
      }
      setTocStructureModalOpen(false);
      onTocStructureApproved?.(data);
    } catch (error) {
      setTocStructureError(error?.message || 'Failed to approve TOC structure.');
    } finally {
      setTocStructureApplying(false);
    }
  };

  useEffect(() => {
    setTocStructureContext(null);
    setTocStructureApproved(false);
    setTocStructureError('');
  }, [tocStructureReviewKey]);

  useEffect(() => {
    if (!needsTocStructureApproval || status !== 'sections_ready') return;
    if (
      tocStructureAutoOpenKey === tocStructureReviewKey
      || tocStructureModalOpen
      || tocStructureContext
      || tocStructureLoading
    ) {
      return;
    }
    setTocStructureAutoOpenKey(tocStructureReviewKey);
    loadTocStructureContext();
  }, [
    loadTocStructureContext,
    needsTocStructureApproval,
    status,
    tocStructureAutoOpenKey,
    tocStructureContext,
    tocStructureLoading,
    tocStructureModalOpen,
    tocStructureReviewKey,
  ]);

  const openUpdateModal = () => {
    if (!canUploadUpdatedBrd || !onUploadUpdatedBrd || isBusy) return;
    setUpdatedBrdFile(null);
    setUpdatedBrdError('');
    setUpdateModalOpen(true);
  };

  const closeUpdateModal = () => {
    if (isUploadingUpdatedBrd) return;
    setUpdateModalOpen(false);
    setUpdatedBrdFile(null);
    setUpdatedBrdError('');
  };

  const handleUpdatedBrdSelection = (event) => {
    const selectedFile = event.target?.files?.[0] || null;
    setUpdatedBrdFile(selectedFile);
    setUpdatedBrdError('');
  };

  const submitUpdatedBrd = async () => {
    if (!updatedBrdFile) {
      setUpdatedBrdError('Choose the updated BRD document before uploading.');
      return;
    }
    if (!onUploadUpdatedBrd) {
      setUpdatedBrdError('Updated BRD upload is not available.');
      return;
    }

    setUpdatedBrdError('');
    try {
      await onUploadUpdatedBrd(updatedBrdFile);
      closeUpdateModal();
    } catch (err) {
      setUpdatedBrdError(err?.message || 'Failed to upload updated BRD.');
    }
  };

  const handleTemplateDownload = async () => {
    if (!onDownloadDgsTemplate || isBusy) return;
    setDgsTemplateError(null);
    try {
      await onDownloadDgsTemplate();
    } catch (err) {
      setDgsTemplateError(err.message || 'Failed to download DGS template');
    }
  };

  const triggerDgsPicker = () => {
    if (!dgsInputRef.current || isBusy) return;
    dgsInputRef.current.value = '';
    dgsInputRef.current.click();
  };

  const handleDgsFileSelection = async (event) => {
    const selectedFile = event.target?.files?.[0];
    if (!selectedFile) return;

    if (!onUploadDgs) {
      setDgsUploadError('DGS upload is not available in this environment.');
      return;
    }

    setDgsUploadError(null);
    setDgsTemplateError(null);
    setIsUploadingDgs(true);
    try {
      await onUploadDgs(selectedFile);
    } catch (err) {
      setDgsUploadError(err.message || 'Failed to parse DGS sheet');
    } finally {
      setIsUploadingDgs(false);
      if (event.target) event.target.value = '';
    }
  };

  const clearDgsSelection = () => {
    if (isBusy) return;
    setDgsUploadError(null);
    if (onClearDgs) onClearDgs();
  };

  const formatTocSectionLabel = (section = {}) => {
    const number = String(section?.section_number || '').trim();
    const title = String(section?.title || section?.section_title || '').trim();
    if (number && title) return `${number} · ${title}`;
    return title || number || 'Untitled section';
  };

  const renderTocStructureRows = (rows = []) => (
    <div className="max-h-[52vh] space-y-2 overflow-auto pr-1">
      {rows.length === 0 ? (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-500">
          No TOC sections available.
        </p>
      ) : rows.map((section, index) => (
        <div
          key={`${section.section_number || index}-${section.identity_key || section.title || index}`}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2"
        >
          <p className="text-sm font-semibold text-slate-900">{formatTocSectionLabel(section)}</p>
          {(section.identity_path || section.path) && (
            <p className="mt-1 truncate text-xs text-slate-500">{section.identity_path || section.path}</p>
          )}
          <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-500">
            {section.indexed_pages && <span>Pages {section.indexed_pages}</span>}
            {Number(section.text_length || 0) > 0 && <span>{Number(section.text_length || 0)} chars</span>}
          </div>
        </div>
      ))}
    </div>
  );

  const renderTocStructureModal = () => {
    if (!tocStructureModalOpen || !tocStructureContext) return null;

    return (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 px-4 py-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="toc-structure-review-title"
      >
        <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">
                TOC Structure Review
              </p>
              <h3 id="toc-structure-review-title" className="mt-1 text-lg font-bold text-slate-900">
                Compare BRD v{tocStructureContext.old_version || '-'} and v{tocStructureContext.new_version || '-'} before continuing
              </h3>
              <p className="mt-1 text-sm leading-5 text-slate-600">
                The extracted structure changed too much for an automatic delta to be trusted. Review the old and new TOCs, then approve to open the extracted sections list.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setTocStructureModalOpen(false)}
              disabled={tocStructureApplying}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
              aria-label="Close TOC structure review"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h4 className="text-sm font-bold text-slate-900">Previous BRD TOC</h4>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-600">
                    {(tocStructureContext.old_sections || []).length} sections
                  </span>
                </div>
                {renderTocStructureRows(tocStructureContext.old_sections || [])}
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h4 className="text-sm font-bold text-slate-900">Updated BRD TOC</h4>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-600">
                    {(tocStructureContext.new_sections || []).length} sections
                  </span>
                </div>
                {renderTocStructureRows(tocStructureContext.new_sections || [])}
              </div>
            </div>

            <label className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm leading-6 text-amber-900">
              <input
                type="checkbox"
                checked={tocStructureApproved}
                onChange={(event) => setTocStructureApproved(event.target.checked)}
                className="mt-1"
              />
              <span>I reviewed both TOCs and approve continuing with this extracted structure.</span>
            </label>

            {tocStructureError && (
              <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm leading-5 text-rose-700">
                {tocStructureError}
              </p>
            )}
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setTocStructureModalOpen(false)}
              disabled={tocStructureApplying}
              className="secondary-btn justify-center px-4 py-2"
            >
              Review Later
            </button>
            <button
              type="button"
              onClick={approveTocStructure}
              disabled={tocStructureApplying}
              className="primary-btn justify-center px-4 py-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {tocStructureApplying ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Approve & Continue
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      {renderTocStructureModal()}
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <header className="flex flex-col gap-4 border-b border-slate-200 bg-slate-50/70 p-5 md:flex-row md:items-center md:justify-between md:px-6">
          <div className="flex min-w-0 items-start gap-3">
            <div className="mt-0.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-indigo-200 bg-white text-indigo-700 shadow-sm">
              {isExtracting ? (
                <Loader className="h-5 w-5 animate-spin" />
              ) : (
                <FileText className="h-5 w-5" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-700">
                Build Sections
              </p>
              <h2 className="mt-1 text-2xl font-bold leading-tight text-slate-900 md:text-[1.7rem]">
                {isExtracting ? 'Building Sections...' : 'Ready to Build Sections'}
              </h2>
              <p className="mt-1 max-w-2xl text-sm leading-5 text-slate-600">
                Create QA-ready sections from the BRD. Use the document outline or let the system find product features.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 md:shrink-0 md:justify-end">
            <div className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700">
              <span>Mode</span>
              <span className="whitespace-nowrap font-semibold">BRD only</span>
            </div>
            <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${
              hasDgsConfig
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-amber-200 bg-amber-50 text-amber-700'
            }`}>
              <span>DGS</span>
              <span className="whitespace-nowrap font-semibold">{hasDgsConfig ? 'Uploaded' : 'Optional'}</span>
            </div>
          </div>
        </header>

        <div className="grid gap-4 p-5 md:grid-cols-2 md:p-6">
          <article className="flex flex-col rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-4 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
              <div className="row-span-2 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
                <FileText className="h-4 w-4" />
              </div>
              <h3 className="min-w-0 text-sm font-semibold leading-5 text-slate-900">Section Source</h3>
              <span className="justify-self-start whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                {extractionModeLabel}
              </span>
            </div>
            <p className="text-sm leading-6 text-slate-600">
              Document Outline reads the BRD table of contents first. If it is missing or unreliable, the system builds one from the PDF layout.
            </p>
            <p className="mt-2 text-xs font-medium leading-5 text-amber-700">
              Find Features scans the BRD and groups requirements by product feature.
            </p>
            <div className="mt-auto pt-4">
              <div className="grid grid-cols-2 rounded-lg border border-slate-200 bg-slate-100 p-1">
                <button
                  type="button"
                  onClick={() => onExtractionModeChange?.('auto')}
                  disabled={isBusy}
                  className={`min-h-9 rounded-md px-3 py-2 text-xs font-semibold transition ${
                    extractionMode !== 'features'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-slate-700 hover:bg-white'
                  }`}
                >
                  Document Outline
                </button>
                <button
                  type="button"
                  onClick={() => onExtractionModeChange?.('features')}
                  disabled={isBusy}
                  className={`min-h-9 rounded-md px-3 py-2 text-xs font-semibold transition ${
                    extractionMode === 'features'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-slate-700 hover:bg-white'
                  }`}
                >
                  Find Features
                </button>
              </div>
            </div>
          </article>

          <article className="flex flex-col rounded-xl border border-indigo-200 bg-white p-4 shadow-[0_10px_28px_-22px_rgba(37,99,235,0.55)]">
            <div className="mb-4 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
              <div className="row-span-2 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
                <Download className="h-4 w-4" />
              </div>
              <h3 className="min-w-0 text-sm font-semibold leading-5 text-slate-900">DGS Validation</h3>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                hasDgsConfig ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
              } justify-self-start whitespace-nowrap`}>
                {hasDgsConfig ? 'Configured' : 'Not configured'}
              </span>
            </div>

            {hasDgsConfig ? (
              <div className="rounded-lg border border-emerald-100 bg-emerald-50/70 px-3 py-2">
                <p className="truncate text-sm font-medium text-slate-900">{dgsConfig.filename}</p>
                <p className="mt-1 text-xs text-slate-600">Uploaded and ready for generation guidance.</p>
              </div>
            ) : (
              <p className="text-sm leading-6 text-slate-600">
                Add an optional Excel sheet to guide constraints and coverage expectations.
              </p>
            )}

            <p className="mt-2 text-[11px] text-slate-500">Accepted: `.xlsx`, `.xlsm`</p>
            {dgsUploadError && <p className="mt-2 text-xs text-red-700">{dgsUploadError}</p>}
            {dgsTemplateError && <p className="mt-1 text-xs text-red-700">{dgsTemplateError}</p>}

            <div className="mt-auto space-y-2 pt-4">
              <button
                type="button"
                onClick={triggerDgsPicker}
                disabled={isBusy}
                className="secondary-btn w-full justify-center border-cyan-200 bg-cyan-50 px-3 py-2 text-cyan-700 hover:bg-cyan-100"
              >
                {isUploadingDgs ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Parsing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    {hasDgsConfig ? 'Replace DGS' : 'Upload DGS'}
                  </>
                )}
              </button>

              <div className={`grid gap-2 ${hasDgsConfig ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <button
                  type="button"
                  onClick={handleTemplateDownload}
                  disabled={isBusy}
                  className="secondary-btn w-full justify-center border-indigo-200 bg-indigo-50 px-3 py-1.5 text-indigo-700 hover:bg-indigo-100"
                >
                  <Download className="h-4 w-4" />
                  Template
                </button>

                {hasDgsConfig ? (
                  <button
                    type="button"
                    onClick={clearDgsSelection}
                    disabled={isBusy}
                    className="secondary-btn w-full justify-center border-rose-200 bg-rose-50 px-3 py-1.5 text-rose-700 hover:bg-rose-100"
                  >
                    <X className="h-4 w-4" />
                    Remove
                  </button>
                ) : null}
              </div>
            </div>

            <input
              ref={dgsInputRef}
              type="file"
              accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
              className="hidden"
              onChange={handleDgsFileSelection}
            />
          </article>

        </div>

        {extractionJob && (
          <div className="section-extraction-progress border-t border-slate-200 bg-indigo-50/40 px-5 py-4 md:px-6">
            <JobProgressCard
              job={extractionJob}
              items={itemsById[extractionJob.id] || []}
              onCancel={cancel}
              onRetry={retry}
            />
            <p className="mt-2 text-xs leading-5 text-slate-600">
              You can leave this page safely. Progress and the completed sections will be restored when you return.
            </p>
          </div>
        )}

        {reusableSectionsAvailable && (
          <div className={`border-t px-5 py-4 md:px-6 ${
            reusableSectionsStale
              ? 'border-amber-200 bg-amber-50/80'
              : 'border-emerald-200 bg-emerald-50/80'
          }`}>
            <div className="flex min-w-0 items-start gap-3">
              <span className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                reusableSectionsStale
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-emerald-100 text-emerald-700'
              }`}>
                {reusableSectionsStale ? (
                  <RefreshCw className="h-4 w-4" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
              </span>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-slate-900">
                  {reusableSectionsStale ? 'Extraction settings changed' : 'Previously extracted sections found'}
                </h3>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  {reusableSectionsStale
                    ? 'Run extraction again so the section map matches the selected mode.'
                    : `${reusableCount || 'Saved'} ${reusableItemLabel} are ready to reuse for this BRD.`}
                </p>
              </div>
            </div>
          </div>
        )}

        {hasChangeSummary && (
          <div className={changeSummaryPanelClass}>
            <div className="flex min-w-0 items-start gap-3">
              <span className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${changeSummaryIconClass}`}>
                {isStructureBlocked || isStructureWarning ? (
                  <AlertCircle className="h-4 w-4" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-slate-900">
                  {isStructureBlocked
                    ? `BRD TOC structure review v${changeSummary.old_version || '-'} to v${changeSummary.new_version || '-'}`
                    : `BRD delta analysis v${changeSummary.old_version || '-'} to v${changeSummary.new_version || '-'}`}
                </h3>
                <div className="mt-2 grid gap-2 sm:grid-cols-4">
                  <div className="rounded-lg border border-blue-100 bg-white px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Added</p>
                    <p className="mt-1 text-lg font-bold text-emerald-700">{Number(changeSummary.added || 0)}</p>
                  </div>
                  <div className="rounded-lg border border-blue-100 bg-white px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Modified</p>
                    <p className="mt-1 text-lg font-bold text-blue-700">{Number(changeSummary.modified || 0)}</p>
                  </div>
                  <div className="rounded-lg border border-blue-100 bg-white px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Deleted</p>
                    <p className="mt-1 text-lg font-bold text-rose-700">{Number(changeSummary.deleted || 0)}</p>
                  </div>
                  <div className="rounded-lg border border-blue-100 bg-white px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Total</p>
                    <p className="mt-1 text-lg font-bold text-slate-900">
                      {Number(changeSummary.total_changes || 0)}
                    </p>
                  </div>
                </div>
                <p className={`mt-2 text-xs leading-5 ${changeSummaryBodyClass}`}>
                  {isStructureBlocked
                    ? (structureMessage || 'The updated BRD TOC changed too much to produce a reliable section delta. Review or rebuild the outline before approving this version.')
                    : usesGeneratedSectionSummary && Number(rawChangeSummary?.total_changes || 0) !== Number(changeSummary.total_changes || 0)
                      ? `${Number(rawChangeSummary?.total_changes || 0)} source BRD changes roll up to ${Number(changeSummary.total_changes || 0)} displayed section${Number(changeSummary.total_changes || 0) === 1 ? '' : 's'}.`
                      : isStructureWarning
                        ? (structureMessage || 'The TOC structure changed noticeably. Review the extracted sections before regenerating test cases.')
                        : 'Review the changed displayed sections on the extracted sections page.'}
                </p>
                {isStructureBlocked && (
                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                    <div className="rounded-lg border border-amber-200 bg-white/70 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700">Matched</p>
                      <p className="mt-1 text-sm font-bold text-slate-900">
                        {Number(structureMetrics.matched_sections || 0)} / {Math.max(Number(structureMetrics.old_sections_count || 0), Number(structureMetrics.new_sections_count || 0))}
                      </p>
                    </div>
                    <div className="rounded-lg border border-amber-200 bg-white/70 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700">Old TOC</p>
                      <p className="mt-1 text-sm font-bold text-slate-900">{Number(structureMetrics.old_sections_count || 0)} sections</p>
                    </div>
	                    <div className="rounded-lg border border-amber-200 bg-white/70 px-3 py-2">
	                      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700">New TOC</p>
	                      <p className="mt-1 text-sm font-bold text-slate-900">{Number(structureMetrics.new_sections_count || 0)} sections</p>
	                    </div>
	                  </div>
	                )}
	                {needsTocStructureApproval && (
	                  <div className="mt-3 flex flex-col gap-3 rounded-xl border border-amber-200 bg-white/80 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
	                    <p className="text-sm leading-5 text-amber-900">
	                      Extraction is complete, but navigation is paused until the old and new TOCs are approved.
	                    </p>
	                    <button
	                      type="button"
	                      onClick={openTocStructureReview}
	                      disabled={tocStructureLoading}
	                      className="secondary-btn justify-center border-amber-300 bg-white px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60"
	                    >
	                      {tocStructureLoading ? (
	                        <>
	                          <Loader className="h-4 w-4 animate-spin" />
	                          Loading TOC...
	                        </>
	                      ) : (
	                        'Review TOC & Continue'
	                      )}
	                    </button>
	                  </div>
	                )}
	                {tocStructureError && !tocStructureModalOpen && (
	                  <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-5 text-rose-700">
	                    {tocStructureError}
	                  </p>
	                )}
	              </div>
	            </div>
	          </div>
	        )}

        {isPendingBrdVersionExtraction && (
          <div className="border-t border-blue-200 bg-blue-50/80 px-5 py-4 md:px-6">
            <div className="flex min-w-0 items-start gap-3">
              <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-blue-700">
                <RefreshCw className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-slate-900">
                  BRD version v{changeSummary?.new_version || nextBrdVersion} is ready for delta extraction
                </h3>
                <p className="mt-1 text-sm leading-5 text-blue-800">
                  Extract sections to compare against v{changeSummary?.old_version || baseBrdVersion}. The change review will appear on the extracted sections page.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-slate-200 bg-slate-50/70 p-5 md:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0 flex-1 space-y-3">
              <p className="text-sm leading-6 text-slate-600">
                Extraction will run in BRD-only mode.
              </p>

              {!canUploadUpdatedBrd && baseBrd && blockedUpdateReason && !isPendingBrdVersionExtraction && (
                <div className="flex max-w-2xl items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 shadow-sm shadow-amber-100/60">
                  <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white text-amber-700">
                    <AlertCircle className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-amber-950">Base extraction needed first</p>
                    <p className="mt-0.5 text-sm leading-5 text-amber-800">{blockedUpdateReason}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto lg:justify-end">
              {canUploadUpdatedBrd && !isPendingBrdVersionExtraction && (
                <button
                  type="button"
                  onClick={openUpdateModal}
                  disabled={isBusy || !onUploadUpdatedBrd}
                  className="secondary-btn h-10 w-full justify-center whitespace-nowrap border-blue-200 bg-white px-4 text-sm font-semibold text-blue-700 hover:bg-blue-50 sm:w-auto sm:min-w-[158px]"
                  title="Upload New BRD Version"
                >
                  {isUploadingUpdatedBrd ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      Uploading Version...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Upload Updated BRD
                    </>
                  )}
                </button>
              )}

	              {reusableSectionsAvailable && !reusableSectionsStale && (
	                <button
	                  type="button"
	                  onClick={needsTocStructureApproval ? openTocStructureReview : onUseReusableSections}
	                  disabled={isBusy || (needsTocStructureApproval && tocStructureLoading)}
	                  className="secondary-btn h-10 w-full justify-center whitespace-nowrap border-emerald-200 bg-white px-4 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 sm:w-auto sm:min-w-[150px]"
	                  title="Use Existing Sections"
	                >
	                  {needsTocStructureApproval && tocStructureLoading ? (
	                    <>
	                      <Loader className="h-4 w-4 animate-spin" />
	                      Loading...
	                    </>
	                  ) : (
	                    <>
	                      <CheckCircle2 className="h-4 w-4" />
	                      {needsTocStructureApproval ? 'Review TOC' : 'Use Existing'}
	                    </>
	                  )}
	                </button>
	              )}

	              <button
	                onClick={needsTocStructureApproval ? openTocStructureReview : onExtract}
	                disabled={isBusy || (needsTocStructureApproval && tocStructureLoading)}
	                className="primary-btn h-10 w-full justify-center whitespace-nowrap bg-gradient-to-r from-blue-600 to-cyan-600 px-5 text-sm font-semibold hover:from-blue-700 hover:to-cyan-700 sm:w-auto sm:min-w-[142px]"
	              >
	                {isExtracting ? (
	                  <>
	                    <Loader className="h-4 w-4 animate-spin" />
	                    Extracting...
	                  </>
	                ) : needsTocStructureApproval ? (
	                  tocStructureLoading ? (
	                    <>
	                      <Loader className="h-4 w-4 animate-spin" />
	                      Loading TOC...
	                    </>
	                  ) : (
	                    <>Review TOC & Continue</>
	                  )
	                ) : reusableSectionsStale ? (
	                  <>Re-run Extraction</>
                ) : reusableSectionsAvailable ? (
                  <>Extract Again</>
                ) : (
                  <>Start Extraction</>
                )}
              </button>
            </div>
          </div>
        </div>

        {isExtracting && (
          <div className="h-1.5 overflow-hidden bg-indigo-100">
            <div className="h-full w-2/3 animate-pulse rounded-full bg-gradient-to-r from-indigo-500 to-cyan-500" />
          </div>
        )}
      </section>

      {updateModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="updated-brd-title"
        >
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">
                  BRD Versioning
                </p>
                <h3 id="updated-brd-title" className="mt-1 text-lg font-bold text-slate-900">
                  Upload New BRD Version
                </h3>
              </div>
              <button
                type="button"
                onClick={closeUpdateModal}
                disabled={isUploadingUpdatedBrd}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                aria-label="Close updated BRD upload"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-5">
              <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-blue-700">
                  Base BRD
                </p>
                <p className="mt-1 truncate text-sm font-semibold text-slate-900" title={baseBrdLabel}>
                  {baseBrdLabel}
                </p>
                <p className="mt-1 text-xs text-blue-700">
                  Current version v{baseBrdVersion}
                </p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="updated-brd-upload">
                  New BRD version document
                </label>
                <input
                  id="updated-brd-upload"
                  type="file"
                  accept={BRD_DOCUMENT_ACCEPT}
                  onChange={handleUpdatedBrdSelection}
                  disabled={isUploadingUpdatedBrd}
                  className="block w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-50 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-60"
                />
                <p className="mt-1 text-xs text-slate-500">Accepted: .pdf, .doc, .docx</p>
              </div>

              {updatedBrdFile && (
                <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800">
                  <FileText className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 truncate text-sm font-medium" title={updatedBrdFile.name}>
                    {updatedBrdFile.name}
                  </span>
                </div>
              )}

              {updatedBrdError && (
                <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {updatedBrdError}
                </p>
              )}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeUpdateModal}
                disabled={isUploadingUpdatedBrd}
                className="secondary-btn justify-center px-4 py-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitUpdatedBrd}
                disabled={isUploadingUpdatedBrd || !updatedBrdFile}
                className="primary-btn justify-center px-4 py-2"
              >
                {isUploadingUpdatedBrd ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Upload Updated BRD
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
